import type {
  OperationsNode,
  OperationsScheduleNode,
  OperationsTeamNode,
} from '../types/operations.types'
import { toNodeType } from './nodeTypes'

type Raw = Record<string, unknown>

const asRecord = (value: unknown): Raw | null =>
  value && typeof value === 'object' ? (value as Raw) : null

const str = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null

const num = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null

const bool = (value: unknown): boolean | null =>
  typeof value === 'boolean' ? value : null

/**
 * Money and rate fields, kept in whatever form the API sent.
 *
 * These come back as decimal strings ("125.00") at least as often as numbers,
 * so `num` would null out real values. Legacy read them raw off the same
 * payload and round-tripped them unchanged; this does the same.
 */
const money = (value: unknown): number | string | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.length > 0) return value
  return null
}

/**
 * A schedule's teams, in either shape the API uses.
 *
 * Nested children carry `teams` as a bare array, but the ROOT competition wraps
 * it in a `{ data: [...] }` envelope. Reading only the bare form means a
 * competition whose teams hang off the root — one with no divisions at all —
 * renders as an empty tree. Legacy handled this by pre-flattening the root
 * before parsing (`ScheduleTree/index.jsx:144-146`); doing it here covers every
 * level instead of just the one the caller remembered to unwrap.
 */
const rawTeams = (raw: Raw): Raw[] => {
  if (Array.isArray(raw.teams)) return raw.teams as Raw[]
  const envelope = asRecord(raw.teams)
  return envelope && Array.isArray(envelope.data)
    ? (envelope.data as Raw[])
    : []
}

/**
 * Teams sort by seed. A null seed sorts last rather than colliding with 0,
 * which is a real seed value.
 */
const bySeed = (a: OperationsTeamNode, b: OperationsTeamNode): number => {
  if (a.seed === b.seed) return 0
  if (a.seed === null) return 1
  if (b.seed === null) return -1
  return a.seed - b.seed
}

/**
 * Only the node's OWN `image` counts. A nested child also exposes
 * `root_schedule.image`, but that belongs to the ancestor season — using it
 * would paint every division with its parent's crest.
 */
const scheduleImage = (raw: Raw): { full_path?: string } | null => {
  const image = asRecord(raw.image)
  return image && str(image.full_path)
    ? { full_path: image.full_path as string }
    : null
}

const buildTeam = (raw: Raw, scheduleId: string): OperationsTeamNode => {
  const teamId = String(raw.id ?? '')
  return {
    id: `${scheduleId}-${teamId}`,
    type: 'team',
    name: str(raw.name) ?? str(raw.name_full) ?? '',
    teamId,
    scheduleId,
    seed: num(raw.seed),
    seedOrigin: num(raw.seed_origin),
    gamesCount: num(raw.games_count) ?? 0,
    isPlaceholder: raw.is_placeholder === true,
    image: str(raw.image),
    children: [],
  }
}

const buildSchedule = (raw: Raw): OperationsScheduleNode => {
  const scheduleId = String(raw.id ?? '')
  const organization = asRecord(raw.organization)

  const teams = rawTeams(raw)
    .map((team) => buildTeam(team, scheduleId))
    .sort(bySeed)

  const nested = asRecord(raw.children)
  const nestedData =
    nested && Array.isArray(nested.data) ? (nested.data as Raw[]) : []

  return {
    id: scheduleId,
    type: toNodeType(raw.type),
    name: str(raw.name) ?? str(raw.name_full) ?? '',
    scheduleId,
    sortOrder: num(raw.sort_order),
    lockRosterDate: str(raw.lock_roster_date),
    isVisibleStandings: bool(raw.is_visible_standings),
    isRegistrationOn: bool(raw.is_registration_on),
    onlineRegistrationOpen: bool(raw.online_registration_open),
    teamCost: money(raw.team_cost),
    deposit: money(raw.deposit),
    divisionAge: str(raw.division_age),
    divisionGender: str(raw.division_gender),
    divisionGroup: str(raw.division_group),
    dayOfWeek: str(raw.day_of_week),
    isActive: bool(raw.is_active),
    // GET name for the Salesforce ID; the PUT body calls it `import_2_id`.
    salesforceId: str(raw.salesforce_id),
    seasonType: str(raw.season_type),
    seasonCurrency: str(raw.season_currency),
    taxRate: money(raw.tax_rate),
    startsAt: str(raw.starts_at),
    endsAt: str(raw.ends_at),
    organizationName: organization ? str(organization.name) : null,
    organizationIsPublic: organization ? bool(organization.is_public) : null,
    gamesCount: num(raw.games_count),
    // Object form here, unlike a team's plain-string `image`. Passed through
    // untouched so the render site can hand it to `resolveAssetUrl`.
    image: scheduleImage(raw),
    // A schedule has EITHER teams or child schedules, never both in practice.
    // Teams win when present so a division never hides its roster behind an
    // empty `children.data`.
    children: teams.length > 0 ? teams : nestedData.map(buildSchedule),
  }
}

/**
 * Maps `GET /schedules/:id?children=true` into the tree the Operations rail
 * renders. Returns the root as a single-element array so callers can treat the
 * result uniformly as a node list.
 */
export const buildOperationsTree = (payload: unknown): OperationsNode[] => {
  const raw = asRecord(payload)
  if (!raw || !raw.id) return []
  return [buildSchedule(raw)]
}
